/**
 * Lexical primitives for the parked-routine parser (VDE-2 Slice 4): char
 * classes, whitespace handling, ident reading and the qualified-name read.
 *
 * Split out of `routine.parse.ts` to keep each module under the 200-line file
 * budget (ROUND2-MINOR-14) while preserving the byte-faithful contract
 * documented on `routine.parse.ts`. The CREATE-prefix consumption + paren /
 * comma helpers live alongside in `routine.parse.name.ts`.
 *
 * Regex-free (project rule VQE-D10) — every primitive here is a character-
 * level scan that was fuzz-verified byte-identical to the removed regex
 * parser. Do NOT "harmonize" with the shared `readIdentifier` helper: it is
 * comment- and engine-aware and would break the required byte-parity (see the
 * long note at the top of the original `routine.parse.ts`).
 */

import type { QualifiedName } from '../../../sql-grammar/model';

const WS = new Set([' ', '\t', '\n', '\r', '\f', '\v']);

/** ASCII whitespace test. Deliberately narrows to the 6 ASCII whitespace
 *  chars rather than JS `\s` (the removed parser's `skipSpaces`/`trim`/`\s`
 *  matched Unicode whitespace too). SQL never uses non-ASCII whitespace
 *  structurally, so this is a benign, intentional deviation — the same one
 *  `trigger.parse.ts` documents — NOT a faithful copy of the old `\s`. */
export function isWs(c: string | undefined): boolean {
  return c !== undefined && WS.has(c);
}

/** Exact `[A-Za-z0-9_]` class — byte-faithful to the removed parser's
 *  `consumeKeyword` word-boundary test and the bare-`IDENT` continuation
 *  (`$` is NOT a word char there, unlike the trigger scanner's ident class). */
export function isWordChar(c: string | undefined): boolean {
  return c !== undefined
    && ((c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || (c >= '0' && c <= '9') || c === '_');
}

/** Exact `[A-Za-z_]` ident-start class (bare identifiers + the `@param`
 *  name + the dollar-tag `[A-Za-z_]*`). */
export function isIdentStart(c: string | undefined): boolean {
  return c !== undefined && ((c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || c === '_');
}

/** JS `.` semantics WITHOUT the `s` flag — matches every char except
 *  the four line terminators (LF, CR, U+2028 LINE SEPARATOR, U+2029
 *  PARAGRAPH SEPARATOR). Regular spaces / tabs DO match. Used only for
 *  the mssql default-expr lazy `(.+?)` group, where byte-parity with
 *  `.+?` is required. */
export function isDotChar(c: string): boolean {
  return c !== '\n' && c !== '\r' && c !== '\u2028' && c !== '\u2029';
}

export function skipSpacesLocal(source: string, start: number, end: number): number {
  let i = start;
  while (i < end && isWs(source[i])) i++;
  return i;
}

/** ASCII-ws trim of `[start, end)` — mirrors the removed parser's leading
 *  `stmtText.trim()` (all offsets it computed were relative to the trimmed
 *  string). Unicode-ws deviation as documented on `isWs`. */
export function trimRange(source: string, start: number, end: number): { s: number; e: number } {
  let s = start;
  let e = end;
  while (s < e && isWs(source[s])) s++;
  while (e > s && isWs(source[e - 1])) e--;
  return { s, e };
}

/** ASCII-ws `String.trim()` proxy on an already-isolated substring. */
export function strTrim(x: string): string {
  let s = 0;
  let e = x.length;
  while (s < e && isWs(x[s])) s++;
  while (e > s && isWs(x[e - 1])) e--;
  return x.slice(s, e);
}

/** Try to consume the unquoted, word-bounded `keyword` at `i` (case-
 *  insensitive). Returns the index just past it, or `-1` when it does not
 *  match — mirrors the removed parser's `consumeKeyword` (rejects when the
 *  following char is `[A-Za-z0-9_]`).
 *
 *  NOTE: the trailing-boundary test is intentionally gated on
 *  `i + keyword.length < end` (a keyword that ends exactly at `end` has no
 *  following char to break the word boundary), unlike the co-located
 *  `trigger.parse.ts:consumeWord`. This preserves byte-parity with the removed
 *  routine parser, which ran on the fully-trimmed statement string. Do NOT
 *  "harmonize" by switching to trigger's exported `consumeWord` — that would
 *  silently drop this end-of-range guard and break parity. */
export function consumeWord(source: string, i: number, end: number, keyword: string): number {
  if (i + keyword.length > end) return -1;
  if (source.slice(i, i + keyword.length).toUpperCase() !== keyword) return -1;
  const next = source[i + keyword.length];
  if (next !== undefined && i + keyword.length < end && isWordChar(next)) return -1;
  return i + keyword.length;
}

export function unquoteIdent(raw: string): { name: string; quoted: boolean } {
  if (raw.length >= 2) {
    const first = raw.charAt(0);
    const last = raw.charAt(raw.length - 1);
    if ((first === '"' && last === '"') || (first === '`' && last === '`')) {
      return { name: raw.slice(1, -1), quoted: true };
    }
    if (first === '[' && last === ']') {
      return { name: raw.slice(1, -1), quoted: true };
    }
  }
  return { name: raw, quoted: false };
}

function buildQualifiedName(rawHead: string, rawTail: string | undefined): QualifiedName {
  if (rawTail !== undefined) {
    const head = unquoteIdent(rawHead);
    const tail = unquoteIdent(rawTail);
    return {
      schema: head.name,
      name: tail.name,
      raw: `${rawHead}.${rawTail}`,
      quoted: head.quoted || tail.quoted,
      foldedName: tail.name.toLowerCase(),
      foldedSchema: head.name.toLowerCase(),
    };
  }
  const ident = unquoteIdent(rawHead);
  return {
    name: ident.name,
    raw: rawHead,
    quoted: ident.quoted,
    foldedName: ident.name.toLowerCase(),
  };
}

/** Read one `IDENT` token at `i`: `[A-Za-z_][A-Za-z0-9_]*` (NO `$`) OR a
 *  `"…"` / `` `…` `` / `[…]` quoted run with ≥1 inner char. Byte-faithful to
 *  the removed parser's `IDENT` alternation. Returns the raw token + end, or
 *  null. */
function readIdentRaw(
  source: string,
  i: number,
  end: number,
): { raw: string; end: number } | null {
  const c = source[i];
  if (c === '"') {
    let j = i + 1;
    while (j < end && source[j] !== '"') j++;
    if (j >= end || j === i + 1) return null;
    return { raw: source.slice(i, j + 1), end: j + 1 };
  }
  if (c === '`') {
    let j = i + 1;
    while (j < end && source[j] !== '`') j++;
    if (j >= end || j === i + 1) return null;
    return { raw: source.slice(i, j + 1), end: j + 1 };
  }
  if (c === '[') {
    let j = i + 1;
    while (j < end && source[j] !== ']') j++;
    if (j >= end || j === i + 1) return null;
    return { raw: source.slice(i, j + 1), end: j + 1 };
  }
  if (isIdentStart(c)) {
    let j = i + 1;
    while (j < end && isWordChar(source[j])) j++;
    return { raw: source.slice(i, j), end: j };
  }
  return null;
}

export interface RoutineNameRead {
  readonly name: QualifiedName;
  readonly end: number;
}

/** Read a `[schema.]name` qualified name at `i`, byte-faithful to the removed
 *  parser's `QUALIFIED_NAME_RE` (`^(IDENT)(?:\s*\.\s*(IDENT))?` — lower-cased
 *  folds, all quote styles, `\s*.\s*` around the dot). Returns null when no
 *  identifier starts at `i`. */
export function readRoutineName(
  source: string,
  i: number,
  end: number,
): RoutineNameRead | null {
  const head = readIdentRaw(source, i, end);
  if (!head) return null;
  const afterHead = skipSpacesLocal(source, head.end, end);
  if (afterHead < end && source[afterHead] === '.') {
    const afterDot = skipSpacesLocal(source, afterHead + 1, end);
    const tail = readIdentRaw(source, afterDot, end);
    if (tail) {
      return { name: buildQualifiedName(head.raw, tail.raw), end: tail.end };
    }
  }
  return { name: buildQualifiedName(head.raw, undefined), end: head.end };
}
